import React, { useEffect, useRef } from "react";
import GeoJSON from "ol/format/GeoJSON";
import VectorSource from "ol/source/Vector";
import VectorLayer from "ol/layer/Vector";
import Feature from "ol/Feature";
import { Geometry } from "ol/geom";
import { useMap } from "../../hooks";
import { useLazyRef } from "../../hooks/useLazyRef";
import { Coordinate } from "./GeoJsonLayer";
import { FeatureCollection } from "../..";
import { getIssueFeatureStyle } from "../../utils/annotationStyleCache";
import {
  computeIssueFeatureDiff,
  getShownFeatures,
  hasStyleRelevantChange,
  pickStyleProps,
} from "./issueFeatureDiff";

/**
 * 이슈/어노테이션 전용 벡터 레이어 props — `properties.type`/`color`/`title`/`fontSize` 로 자동 스타일.
 *
 * `isShown=true` 피처만 렌더링. `FeaturesStore` 가 내부에서 사용 — 일반 GeoJSON 표시는 `GeoJsonLayer`/`StyledGeoJsonLayer`.
 *
 * geoJson 변경 시 레이어/소스/인터랙션을 통째 재생성하지 않고 **id 기준 증분 갱신**(add/remove/update)
 * 한다(D6). 선택·제목토글 등 시각 변화는 해당 피처 property 갱신 + `changed()` 로 그 피처만 재드로우하고,
 * 이동/수정은 geometry 만 교체한다. 참조 모델: `layer/annotation/Polygon.tsx` 의 `CustomPolygon`.
 */
export interface IssueGeoJsonLayerProps {
  geoJson: FeatureCollection;

  /**
   * @default "EPSG:5186"
   * @description You can set coordinate option of geJson.
   */
  projectionCode?: Coordinate;

  zIndex?: number;
}

interface FeatureRecord {
  feature: Feature<Geometry>;
  /** 마지막으로 적용한 geoJson geometry 참조 — 변경(이동/수정) 감지용 */
  srcGeometry: any;
  /** 마지막으로 적용한 스타일 속성 스냅샷 — 재드로우 판단용 */
  srcStyleProps: Record<string, any>;
}

export function IssueGeoJsonLayer({
  geoJson,
  zIndex = 1,
  projectionCode = "EPSG:5186",
}: IssueGeoJsonLayerProps) {
  const map = useMap();

  const fromProjection = projectionCode;
  const toProjection = "EPSG:3857";

  // 안정적인 source/layer 를 1회만 생성한다(증분 렌더의 핵심 — 참조: CustomPolygon).
  const sourceRef = useLazyRef<VectorSource<Feature<Geometry>>>(
    () => new VectorSource<Feature<Geometry>>()
  );
  const layerRef = useLazyRef<VectorLayer<VectorSource<Feature<Geometry>>>>(
    () =>
      new VectorLayer({
        source: sourceRef.current,
        zIndex,
        // 동일 속성 피처는 캐시된 동일 Style 인스턴스를 재사용한다(D4).
        style: (feature) => {
          const properties = feature.getProperties();
          return getIssueFeatureStyle({
            type: properties["type"] as string,
            title: properties["title"] as string,
            isSelected: properties["isSelected"] as boolean,
            isTextShown: properties["isTextShown"] as boolean,
            fontSize: properties["fontSize"] as number,
            color: properties["color"] as string | undefined,
            opacity: properties["opacity"] as number | undefined,
          });
        },
      })
  );

  // id → OL feature 레코드. geoJson diff 시 feature 인스턴스를 안정적으로 유지한다.
  const featureMapRef = useLazyRef<Map<string, FeatureRecord>>(
    () => new Map<string, FeatureRecord>()
  );
  const formatRef = useLazyRef(
    () => new GeoJSON({ extractGeometryName: true })
  );
  const lastProjectionRef = useRef<string | null>(null);

  // 레이어 생명주기 — map 변경 시에만 마운트/언마운트.
  // 표시 전용 레이어이므로 Snap 인터랙션을 붙이지 않는다(D3 — 편집 스냅은 useModifyInteraction 이 보유).
  useEffect(() => {
    const layer = layerRef.current;
    map.addLayer(layer);
    return () => {
      layer.getSource()?.clear();
      featureMapRef.current.clear();
      lastProjectionRef.current = null;
      map.removeLayer(layer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  // zIndex 는 setZIndex 전용 경로로 유지한다(diff 와 분리 — §7.5).
  useEffect(() => {
    layerRef.current.setZIndex(zIndex);
  }, [zIndex]);

  // geoJson 변경 시 id 기준 증분 갱신(add/remove/update) — 전체 재빌드 제거(D6).
  useEffect(() => {
    const source = sourceRef.current;
    const format = formatRef.current;
    const featureMap = featureMapRef.current;

    // projectionCode 가 바뀌면 좌표 변환 기준이 달라지므로 전체 재구성한다(드문 경로).
    if (lastProjectionRef.current !== fromProjection) {
      source.clear();
      featureMap.clear();
      lastProjectionRef.current = fromProjection;
    }

    const desired = getShownFeatures(geoJson);
    const { toAdd, toUpdate, toRemoveIds } = computeIssueFeatureDiff(
      featureMap.keys(),
      desired
    );

    // 삭제: 사라졌거나 isShown=false 가 된 피처
    for (const id of toRemoveIds) {
      const rec = featureMap.get(id);
      if (rec) {
        source.removeFeature(rec.feature);
        featureMap.delete(id);
      }
    }

    // 신규: readFeature → transform → addFeature
    for (const f of toAdd) {
      const olFeature = format.readFeature(f as any) as Feature<Geometry>;
      const geometry = olFeature.getGeometry();
      if (geometry) geometry.transform(fromProjection, toProjection);
      olFeature.setId(String(f.id));
      source.addFeature(olFeature);
      featureMap.set(String(f.id), {
        feature: olFeature,
        srcGeometry: f.geometry,
        srcStyleProps: pickStyleProps(f.properties),
      });
    }

    // 기존: 변경분만 갱신 — 속성 동기화 후, 실제 변화가 있을 때만 해당 피처 재드로우
    for (const f of toUpdate) {
      const rec = featureMap.get(String(f.id));
      if (!rec) continue;
      const olFeature = rec.feature;

      // 속성 동기화 (set 은 change 를 발생시키지 않아 재드로우를 유발하지 않는다)
      olFeature.setProperties(f.properties);

      let redrawn = false;
      // 이동/수정: geometry 참조가 바뀐 경우에만 geometry 교체(= 해당 피처 재드로우)
      if (rec.srcGeometry !== f.geometry) {
        const newGeom = format.readGeometry(f.geometry as any) as Geometry;
        newGeom.transform(fromProjection, toProjection);
        olFeature.setGeometry(newGeom);
        rec.srcGeometry = f.geometry;
        redrawn = true;
      }

      // 선택·제목토글 등 시각 변화: 스타일 속성이 바뀐 경우에만 해당 피처 재드로우
      if (!redrawn && hasStyleRelevantChange(rec.srcStyleProps, f.properties)) {
        olFeature.changed();
      }
      rec.srcStyleProps = pickStyleProps(f.properties);
    }
    // zIndex 는 전용 effect 에서 처리하므로 의존성에서 제외한다(§7.5).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, geoJson, fromProjection]);

  return null;
}
